import { CalendarClock, Check, CirclePause, GitMerge, Pencil, PlugZap, Plus, Save, Snowflake, Trash2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { FreezeBlock, FreezeWindow, LandingReviewer, PolicyConfig } from '@orgus/protocol';
import { FREEZE_BLOCKS } from '@orgus/protocol';
import { freezeUntil, landingDecision } from '@orgus/policy';
import { Badge, Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, Input, Panel, REVEAL_GROUP, RelativeTime, RevealSlot, Segmented, Select, Skeleton, Textarea, cn, plural, toUiError, toast, useNow } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { deleteFreeze, saveFreeze, saveSettings, usePolicyV2 } from './live.ts';
import { DAY_SHORT, REVIEWER_LABEL, describeFreeze, emptyFreezeForm, freezeFormOf, freezeInputOf } from './model.ts';
import type { FreezeForm } from './model.ts';
import { useProjects } from './LaunchSettingsView.tsx';

const BLOCK_LABEL: Record<FreezeBlock, string> = { landing: 'Landing', deploy: 'Deploys', 'session.start': 'New sessions' };

function FreezeEditor({ open, onOpenChange, editing }: { open: boolean; onOpenChange: (o: boolean) => void; editing: FreezeWindow | null }) {
  const { list } = useProjects();
  const [form, setForm] = useState<FreezeForm>(() => emptyFreezeForm(Date.now()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setForm(editing ? freezeFormOf(editing, Date.now()) : emptyFreezeForm(Date.now()));
    setError(null);
    setBusy(false);
  }, [open, editing]);
  const set = <K extends keyof FreezeForm>(k: K, v: FreezeForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = <T,>(arr: T[], v: T) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async () => {
    const v = freezeInputOf(form, -new Date().getTimezoneOffset());
    if (!v.ok) return setError(v.error);
    setBusy(true);
    setError(null);
    try {
      const w = await saveFreeze(v.input, editing?.id);
      toast(editing ? 'Freeze window updated' : 'Freeze window added', { tone: 'success', description: `${w.name}: ${describeFreeze(w)}.` });
      onOpenChange(false);
    } catch (e) {
      const u = toUiError(e);
      setError(`${u.what} ${u.why ?? ''} ${u.next ?? ''}`.replace(/\s+/g, ' ').trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={editing ? 'Edit freeze window' : 'New freeze window'}
      description="What it stops is refused for everyone, people and agents alike."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            {editing ? 'Save window' : 'Add window'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Release week" />
          <Select label="Applies to" value={form.scope} onValueChange={(v) => set('scope', v)} options={[{ value: 'org', label: 'Every project' }, ...list.map((p) => ({ value: p.id, label: p.name }))]} />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-muted">When</span>
          <Segmented<FreezeForm['kind']>
            label="When"
            value={form.kind}
            onChange={(v) => set('kind', v)}
            options={[
              { value: 'once', label: 'Once' },
              { value: 'weekly', label: 'Every week' },
            ]}
          />
        </div>
        {form.kind === 'once' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="From" type="datetime-local" value={form.from} onChange={(e) => set('from', e.target.value)} />
            <Input label="To" type="datetime-local" value={form.to} onChange={(e) => set('to', e.target.value)} />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days">
              {DAY_SHORT.map((d, i) => (
                <Button key={d} size="sm" variant={form.days.includes(i) ? 'primary' : 'outline'} aria-pressed={form.days.includes(i)} onClick={() => set('days', toggle(form.days, i))}>
                  {d}
                </Button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="From" type="time" value={form.start} onChange={(e) => set('start', e.target.value)} />
              <Input label="To" type="time" value={form.end} onChange={(e) => set('end', e.target.value)} hint="Your time zone; an end before the start runs into the next day." />
            </div>
          </>
        )}
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium text-fg-muted">Stops</legend>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="What the window stops">
            {FREEZE_BLOCKS.map((b) => (
              <Button key={b} size="sm" variant={form.blocks.includes(b) ? 'primary' : 'outline'} aria-pressed={form.blocks.includes(b)} icon={form.blocks.includes(b) ? <Check /> : undefined} onClick={() => set('blocks', toggle(form.blocks, b))}>
                {BLOCK_LABEL[b]}
              </Button>
            ))}
          </div>
        </fieldset>
        <Textarea label="Why (shown when something is refused)" rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="v2.4 is in final QA." />
        <button type="submit" hidden />
      </form>
      {error && (
        <p role="alert" className="mt-4 rounded-md bg-failed-soft px-3 py-2 text-sm text-failed">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function Freezes({ config }: { config: PolicyConfig }) {
  const now = useNow(30_000);
  const { name } = useProjects();
  const [editor, setEditor] = useState<{ open: boolean; w: FreezeWindow | null }>({ open: false, w: null });
  const [removing, setRemoving] = useState<FreezeWindow | null>(null);
  const rows = useMemo(() => [...config.freezes].sort((a, b) => Number(freezeUntil(b, now) != null) - Number(freezeUntil(a, now) != null) || a.name.localeCompare(b.name)), [config.freezes, now]);
  const active = rows.filter((w) => freezeUntil(w, now) != null).length;
  const narrow = useMediaQuery(NARROW_QUERY);
  return (
    <Panel
      title="Freeze windows"
      sub={narrow ? (active ? undefined : rows.length) : active ? `${active} in force now` : plural(rows.length, 'window')}
      icon={<Snowflake />}
      flush
      actions={
        <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditor({ open: true, w: null })}>
          New window
        </Button>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          icon={<Snowflake />}
          title="No freeze windows"
          description="Pause landing, deploys or new sessions for a release or a quiet night."
          action={
            <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setEditor({ open: true, w: null })}>
              New window
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Freeze windows">
          {rows.map((w) => {
            const until = freezeUntil(w, now);
            const off = w.enabled === false;
            const project = w.scope.kind === 'org' ? 'Every project' : (name(w.scope.projectId) ?? 'Unknown project');
            const stops = w.blocks.map((b, i) => (i ? BLOCK_LABEL[b].toLowerCase() : BLOCK_LABEL[b])).join(', ');
            return (
              // Fixed columns on a desktop (status, window, project, what it stops, actions on hover).
              // Below 768 px: status and actions on line one, then the name, the window and the rest.
              <li key={w.id} className={cn(REVEAL_GROUP, 'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5', off && 'opacity-60')}>
                <span className="order-1 shrink-0 md:w-32">
                  {off ? (
                    <span className="inline-flex items-center gap-1 text-sm text-fg-subtle [&_svg]:size-3.5">
                      <CirclePause aria-hidden />
                      Off
                    </span>
                  ) : until != null ? (
                    <Badge tone="waiting" icon={<Snowflake />}>
                      Frozen now
                    </Badge>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-fg-subtle [&_svg]:size-3.5">
                      <CalendarClock aria-hidden />
                      Scheduled
                    </span>
                  )}
                </span>
                <div className="order-last flex min-w-0 basis-full flex-col gap-0.5 md:order-2 md:basis-0 md:flex-1">
                  <span className="text-sm font-medium text-fg md:truncate">{w.name}</span>
                  <span className="text-sm text-fg-muted md:truncate">
                    {describeFreeze(w)}
                    {until != null && (
                      <>
                        , ends <RelativeTime ts={until} />
                      </>
                    )}
                  </span>
                  {w.reason && <span className="text-sm text-fg-subtle md:truncate">{w.reason}</span>}
                  <span className="text-caption text-fg-subtle md:hidden">
                    {project} · {stops}
                  </span>
                </div>
                <span className="order-3 hidden w-36 shrink-0 truncate text-sm text-fg-muted md:inline" title={project}>
                  {project}
                </span>
                <span className="order-4 hidden w-40 shrink-0 truncate text-sm text-fg-muted md:inline" title={`Stops ${stops.toLowerCase()}`}>
                  {stops}
                </span>
                <RevealSlot className="order-2 ml-auto flex shrink-0 gap-0.5 md:order-5 md:ml-0">
                  <IconButton size="xs" label={`Edit ${w.name}`} icon={<Pencil />} onClick={() => setEditor({ open: true, w })} />
                  <IconButton size="xs" label={`Delete ${w.name}`} icon={<Trash2 />} variant="danger" onClick={() => setRemoving(w)} />
                </RevealSlot>
              </li>
            );
          })}
        </ul>
      )}
      <FreezeEditor open={editor.open} editing={editor.w} onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing ? `Delete freeze "${removing.name}"?` : 'Delete freeze?'}
        description={removing ? `${describeFreeze(removing)}. ${freezeUntil(removing, now) != null ? 'It is in force now; deleting it ends the freeze at once.' : 'It stops applying.'} You can restore it from Policy versions.` : ''}
        confirmLabel="Delete window"
        onConfirm={async () => {
          if (removing) await deleteFreeze(removing);
          toast('Freeze window deleted', { tone: 'neutral' });
        }}
      />
    </Panel>
  );
}

function LandingRules({ config }: { config: PolicyConfig }) {
  const { list } = useProjects();
  const now = useNow(30_000);
  const [scope, setScope] = useState('org');
  const layer = scope === 'org' ? config.org : config.projects[scope];
  const [checks, setChecks] = useState('');
  const [reviewer, setReviewer] = useState('inherit');
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify(layer ?? {});
  useEffect(() => {
    setChecks(layer?.requiredChecks?.join(', ') ?? '');
    setReviewer(layer?.reviewer ?? 'inherit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, key]);

  const save = async () => {
    setBusy(true);
    try {
      const { requiredChecks: _c, reviewer: _r, ...rest } = layer ?? {};
      const list2 = checks.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
      await saveSettings(scope === 'org' ? { kind: 'org' } : { kind: 'project', projectId: scope }, { ...rest, ...(checks.trim() ? { requiredChecks: list2 } : {}), ...(reviewer !== 'inherit' ? { reviewer: reviewer as LandingReviewer } : {}) });
      toast('Landing rules saved', { tone: 'success', description: 'The landing queue checks them for the next item.' });
    } catch (e) {
      const u = toUiError(e);
      toast(`Could not save: ${u.what}`, { tone: 'error', description: u.next });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Landing rules"
      icon={<GitMerge />}
      actions={<Select aria-label="Layer" size="sm" value={scope} onValueChange={setScope} options={[{ value: 'org', label: 'Every project' }, ...list.map((p) => ({ value: p.id, label: p.name }))]} />}
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Required checks" value={checks} onChange={(e) => setChecks(e.target.value)} placeholder={scope === 'org' ? 'tests (built in)' : `${config.org.requiredChecks?.join(', ') ?? 'tests'} (inherited)`} hint="Comma separated, for example tests, lint, review." spellCheck={false} className="font-mono" />
          <Select
            label="Review before landing"
            value={reviewer}
            onValueChange={setReviewer}
            options={[{ value: 'inherit', label: scope === 'org' ? 'Built in (no review)' : 'Inherit' }, ...Object.entries(REVIEWER_LABEL).map(([value, label]) => ({ value, label }))]}
          />
        </div>
        <div className="flex justify-end">
          <Button variant="primary" icon={<Save />} loading={busy} onClick={() => void save()}>
            Save landing rules
          </Button>
        </div>
        {list.length > 0 && (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle" aria-label="Landing now, per project">
            {list.map((p) => {
              const d = landingDecision(config, p.id, now);
              return (
                <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                  <span className="w-28 shrink-0">
                    {/* The normal state is quiet: a grey check and label. Only a freeze is a pill. */}
                    {d.allowed ? (
                      <span className="inline-flex items-center gap-1 text-sm text-fg-subtle [&_svg]:size-3.5">
                        <Check aria-hidden />
                        Can land
                      </span>
                    ) : (
                      <Badge tone="waiting" icon={<Snowflake />}>
                        Frozen
                      </Badge>
                    )}
                  </span>
                  <span className="w-40 shrink-0 truncate font-medium text-fg">{p.name}</span>
                  <span className="min-w-0 flex-1 truncate text-fg-muted" title={d.reason}>
                    {d.reason}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/** Settings, Landing and freezes: landing rules per layer and freeze windows, both versioned policy. */
export function LandingView() {
  const { phase, policy, config } = usePolicyV2();
  const loadError = useOrgus((s) => s.loadError);
  if (phase === 'loading')
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Loading landing rules">
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[220px]" />
      </div>
    );
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !policy) return <EmptyState icon={<PlugZap />} title="Landing rules are not available on this hub" description="Update Orgus on the hub machine, then restart it." />;
  return (
    <div className="flex max-w-[960px] flex-col gap-6">
      <Freezes config={config} />
      <LandingRules config={config} />
    </div>
  );
}
